import { PspRules } from "@CommonTypes/backend/PspRules";

// The fields the vertical-blocking switch owns. It also turns the Auto-Mudmap
// engine on, which is not a tunable - see the modal. Everything else is left
// alone: the solve budget, the item list, days of supply, the facings cap, the
// rectangle rules the backend derives from the bay count, and cdt0 contiguity,
// which has no field on this request.
export interface VerticalBlockingTunables {
  mudmapLevel: PspRules["mudmap_level"];
  extraFacingMarginalValue: number;
  placeAllVariants: PspRules["high_density_mode"]["place_all_variants"];
  snakingStrategy: PspRules["high_density_mode"]["snaking_strategy"];
  snaking: PspRules["merchandising"]["snaking"];
  cdtStacking: PspRules["merchandising"]["cdt_stacking"];
  fixedNotchNum: PspRules["relax_factors"]["fixed_notch_num"];
  spaceMix: PspRules["relax_factors"]["space_mix"];
  unitsNotEnoughDos: PspRules["relax_factors"]["units_not_enough_dos"];
  unitsNotEnoughMos: PspRules["relax_factors"]["units_not_enough_mos"];
  facingsTooMany: PspRules["relax_factors"]["facings_too_many"];
  minDistribution: PspRules["relax_factors"]["min_distribution"];
  shelfSegmentSpace: PspRules["relax_factors"]["shelf_segment_space"];
  cdt1Contiguous: PspRules["relax_factors"]["cdt1_contiguous"];
  cdt2Contiguous: PspRules["relax_factors"]["cdt2_contiguous"];
  cdt3Contiguous: PspRules["relax_factors"]["cdt3_contiguous"];
  cdt1Order: PspRules["relax_factors"]["cdt1_order"];
  cdt2Order: PspRules["relax_factors"]["cdt2_order"];
  cdt3Order: PspRules["relax_factors"]["cdt3_order"];
  itemIdOrder: PspRules["relax_factors"]["item_id_order"];
  itemSpan: PspRules["relax_factors"]["item_span"];
  cdt0ShelvesMisaligned: number;
  cdt0ShelfSpaceMisalignment: number;
  cdt2ShelfSpaceMisalignment: number;
  cdt0CellXMisalignment: number;
  cdt2CellXMisalignment: number;
}

// The values every vertical-blocking planogram was tested with.
export const verticalBlockingPreset: VerticalBlockingTunables = {
  // Brand columns are planned on the mudmap at brand level, not at cdt2.
  mudmapLevel: "CDT1",
  // A column has to be worth widening, or the solve fills one shelf instead.
  extraFacingMarginalValue: 0.5,
  // Every brand with candidates gets a cell, so no brand loses its column.
  placeAllVariants: true,
  // A column neither snakes nor stacks: both break the line it is read along.
  snakingStrategy: "none",
  snaking: false,
  cdtStacking: false,
  // Notches have to be free for a block to reach the same width on every shelf.
  fixedNotchNum: 0.5,
  // Vertical blocking works out its own space shares.
  spaceMix: 0,
  // A column needs a little stock slack to fill top to bottom.
  unitsNotEnoughDos: 0.1,
  unitsNotEnoughMos: 0.2,
  // The facings cap has to bite harder once the cells are cut in advance.
  // Classic rules modal only: O's "Run as is" deliberately sends a hard cap.
  facingsTooMany: 50,
  // A dropped product is the main way a vertical wall fails.
  minDistribution: 100,
  // Without this, small brands condense onto one shelf instead of standing up.
  shelfSegmentSpace: 100,
  // Order and contiguity hold the blocks together.
  cdt1Contiguous: 50,
  cdt2Contiguous: 50,
  cdt3Contiguous: 50,
  cdt1Order: 50,
  cdt2Order: 50,
  cdt3Order: 50,
  itemIdOrder: 50,
  itemSpan: 50,
  // These make a block a rectangle rather than a staircase.
  cdt0ShelvesMisaligned: 40,
  cdt0ShelfSpaceMisalignment: 100,
  cdt2ShelfSpaceMisalignment: 100,
  cdt0CellXMisalignment: 100,
  cdt2CellXMisalignment: 100,
};
